import { isSameWeek, startOfWeek, getDay, isBefore, isAfter, isSameDay } from 'date-fns';

/**
 * Kiểm tra xem ngày có phải là thứ 3 của tuần hiện tại không
 * @param {Date} date - Ngày cần kiểm tra
 * @returns {boolean}
 */
export const isWednesdayOfCurrentWeek = (date) => {
  const today = new Date();
  const targetDate = new Date(date);
  
  // Kiểm tra xem có cùng tuần không
  if (!isSameWeek(targetDate, today, { weekStartsOn: 1 })) {
    return false;
  }
  
  // Kiểm tra xem có phải thứ 3 không (getDay() trả về 0-6, thứ 3 = 2)
  return getDay(targetDate) === 2;
};

/**
 * Kiểm tra xem ngày có phải là sau thứ 3 của tuần hiện tại không
 * @param {Date} date - Ngày cần kiểm tra
 * @returns {boolean}
 */
export const isAfterWednesdayOfCurrentWeek = (date) => {
  const today = new Date();
  const targetDate = new Date(date);
  
  // Kiểm tra xem có cùng tuần không
  if (!isSameWeek(targetDate, today, { weekStartsOn: 1 })) {
    return false;
  }
  
  // Lấy thứ 3 của tuần hiện tại
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const wednesdayOfCurrentWeek = new Date(currentWeekStart);
  wednesdayOfCurrentWeek.setDate(currentWeekStart.getDate() + 2); // Thứ 3 = +2 ngày từ thứ 2
  
  // Kiểm tra xem ngày có sau thứ 3 không
  return isAfter(targetDate, wednesdayOfCurrentWeek) || isSameDay(targetDate, wednesdayOfCurrentWeek);
};

/**
 * Kiểm tra xem ngày có phải là trước thứ 3 của tuần hiện tại không
 * @param {Date} date - Ngày cần kiểm tra
 * @returns {boolean}
 */
export const isBeforeWednesdayOfCurrentWeek = (date) => {
  const today = new Date();
  const targetDate = new Date(date);
  
  // Kiểm tra xem có cùng tuần không
  if (!isSameWeek(targetDate, today, { weekStartsOn: 1 })) {
    return false;
  }
  
  // Lấy thứ 3 của tuần hiện tại
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const wednesdayOfCurrentWeek = new Date(currentWeekStart);
  wednesdayOfCurrentWeek.setDate(currentWeekStart.getDate() + 2); // Thứ 3 = +2 ngày từ thứ 2
  
  // Kiểm tra xem ngày có trước thứ 3 không
  return isBefore(targetDate, wednesdayOfCurrentWeek);
};

/**
 * Kiểm tra xem có thể chỉnh sửa/đăng ký ca làm việc không
 * @param {Date} date - Ngày cần kiểm tra
 * @param {Object} regWindow - Thông tin cổng đăng ký
 * @param {boolean} isAdmin - Có phải admin không
 * @returns {Object} - { canEdit: boolean, reason: string }
 */
export const canEditOrRegisterShift = (date, regWindow, isAdmin = false) => {
  const targetDate = new Date(date);
  const today = new Date();
  const targetWeekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
  const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  
  // Admin có thể chỉnh sửa tuần hiện tại và tương lai
  if (isAdmin) {
    if (targetWeekStart < todayWeekStart) {
      return { canEdit: false, reason: 'past_week_admin' };
    }
    return { canEdit: true, reason: 'admin' };
  }
  
  // Nhân viên không thể chỉnh sửa tuần trước
  if (targetWeekStart < todayWeekStart) {
    return { canEdit: false, reason: 'past_week_employee' };
  }
  
  // Nhân viên có thể đăng ký cho tuần hiện tại và tương lai nếu cổng đăng ký mở
  if (targetWeekStart >= todayWeekStart) {
    // Kiểm tra xem cổng đăng ký có mở không
    if (!regWindow?.isOpen) {
      return { canEdit: false, reason: 'registration_closed' };
    }
    
    // Nếu cổng đăng ký mở, cho phép đăng ký bất kể thứ mấy trong tuần
    return { canEdit: true, reason: 'allowed' };
  }
  
  return { canEdit: false, reason: 'unknown' };
};

/**
 * Lấy thông báo lỗi dựa trên lý do hạn chế
 * @param {string} reason - Lý do hạn chế
 * @returns {string}
 */
export const getRestrictionMessage = (reason) => {
  switch (reason) {
    case 'past_week_employee':
      return 'Không thể chỉnh sửa lịch của các tuần trước';
    case 'past_week_admin':
      return 'Admin không thể chỉnh sửa lịch của các tuần trước';
    case 'after_wednesday':
      return 'Từ thứ 3 của tuần hiện tại, chỉ admin mới có thể chỉnh sửa lịch làm việc';
    case 'registration_closed':
      return 'Cổng đăng ký đã đóng';
    case 'admin':
      return 'Admin có quyền chỉnh sửa';
    case 'allowed':
      return 'Có thể chỉnh sửa';
    case 'unknown':
      return 'Không thể xác định quyền chỉnh sửa';
    default:
      return 'Không thể chỉnh sửa';
  }
};

/**
 * Kiểm tra xem có phải thứ 3 của tuần hiện tại không (để hiển thị thông báo)
 * @returns {boolean}
 */
export const isTodayWednesdayOfCurrentWeek = () => {
  const today = new Date();
  return isWednesdayOfCurrentWeek(today);
};

/**
 * Lấy thông báo cảnh báo cho thứ 3
 * @returns {string}
 */
export const getWednesdayWarningMessage = () => {
  return 'Hôm nay là thứ 3! Từ hôm nay, chỉ admin mới có thể chỉnh sửa lịch làm việc.';
};
